import { ChipSelect, ChipSelectInline } from "../partner/ChipSelect";
import { Input } from "@/components/ui/input";
import { Home, RefreshCw, Building2, Wallet } from "lucide-react";

const STRATEGY_OPTIONS = [
  { value: "buy_to_let", label: "Buy-to-Let", icon: Home },
  { value: "flip", label: "Flip / Refurb & Sell", icon: RefreshCw },
  { value: "hmo", label: "HMO Conversion", icon: Building2 },
  { value: "serviced", label: "Serviced Accommodation", icon: Wallet },
];

const CONDITION_OPTIONS = [
  { value: "good", label: "Good" },
  { value: "fair", label: "Fair" },
  { value: "needs_work", label: "Needs Work" },
];

const HOLDING_PERIOD_OPTIONS = [
  { value: "1", label: "1 year" },
  { value: "3", label: "3 years" },
  { value: "5", label: "5 years" },
  { value: "10", label: "10+ years" },
];

const formatCurrencyInput = (value) => {
  const digits = value.replace(/[^\d]/g, '');
  if (!digits) return '';
  return parseInt(digits).toLocaleString('en-GB');
};

export function InvestorContextScreen({ data, onChange }) {
  const updateField = (field, value) => {
    onChange({ ...data, [field]: value });
  };

  const handleCurrencyInput = (field, value) => {
    updateField(field, formatCurrencyInput(value));
  };

  return (
    <div className="space-y-6">
      {/* Investment Strategy */}
      <div>
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
          Investment Strategy
        </label>
        <ChipSelect
          options={STRATEGY_OPTIONS}
          value={data.investment_strategy}
          onChange={(v) => updateField("investment_strategy", v)}
          columns={2}
        />
      </div>

      {/* Property Condition */}
      <div>
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
          Current Condition
        </label>
        <ChipSelectInline
          options={CONDITION_OPTIONS}
          value={data.condition}
          onChange={(v) => updateField("condition", v)}
        />
      </div>

      {/* Financial Inputs */}
      <div className="space-y-4">
        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2 block">
            Purchase Price <span className="text-white/30">(optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">£</span>
            <Input
              type="text"
              value={data.purchase_price}
              onChange={(e) => handleCurrencyInput("purchase_price", e.target.value)}
              placeholder="e.g. 250,000"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-7"
              data-testid="investor-purchase-price"
            />
          </div>
        </div>

        <div>
          <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2 block">
            Renovation Budget <span className="text-white/30">(optional)</span>
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">£</span>
            <Input
              type="text"
              value={data.renovation_budget}
              onChange={(e) => handleCurrencyInput("renovation_budget", e.target.value)}
              placeholder="e.g. 30,000"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-7"
              data-testid="investor-renovation-budget"
            />
          </div>
        </div>

        {data.investment_strategy !== "flip" && (
          <div>
            <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2 block">
              Target Monthly Rent <span className="text-white/30">(optional)</span>
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">£</span>
              <Input
                type="text"
                value={data.target_rent}
                onChange={(e) => handleCurrencyInput("target_rent", e.target.value)}
                placeholder="e.g. 1,500"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-7"
                data-testid="investor-target-rent"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 text-xs">/month</span>
            </div>
          </div>
        )}
      </div>

      {/* Holding Period */}
      <div>
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
          Planned Holding Period
        </label>
        <ChipSelectInline
          options={HOLDING_PERIOD_OPTIONS}
          value={data.holding_period}
          onChange={(v) => updateField("holding_period", v)}
        />
      </div>

      {/* Notes */}
      <div>
        <label className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3 block">
          Notes <span className="text-white/30">(optional)</span>
        </label>
        <textarea
          value={data.notes || ""}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder="Any specific investment considerations..."
          className="w-full bg-white/5 border border-white/10 rounded-xl p-3 text-sm text-white placeholder:text-white/30 resize-none h-20 focus:outline-none focus:border-blue-500/40 transition-colors"
          data-testid="investor-notes"
        />
      </div>
    </div>
  );
}

export function isInvestorContextComplete(data) {
  return (
    data.investment_strategy &&
    data.condition &&
    data.holding_period
  );
}
